import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";

import CommentsList from "components/lib/Global/CommentsList";
import { Constants } from "config/constants";
import { ExclamationIcon } from "@heroicons/react/outline";
import { ChevronLeftIcon } from "components/lib/Shared/Icons/sflIcons";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import {
  updateCompanyAsync,
  updateManualTransactions,
} from "state/slices/company";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { useToast } from "hooks/useToast";
import UpdateCompanyDetailsContext from "context/UpdateCompanyDetails";

export default function Status({
  submitToLabel,
  setDisableSubmitBtn,
  agreeToSubmit,
  agreeToTerms,
  setAgreeToSubmit,
  setAgreeToTerms,
  handleGoBack,
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const { comments } = useSelector((state) => state.commentsSlice);
  const { transactions } = useContext(UpdateCompanyDetailsContext);
  const unresolvedComments = comments.filter((comment) => !comment.isResolved);
  let transactionIds = [];
  if (transactions?.length > 0) {
    transactionIds = transactions.map((trx) => trx?._id);
  }
  let status = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const isReviewer = Cookies.get("role") === Constants.COOKIE_ROLES.REVIEWER;
  const isAdmin = [
    Constants.COOKIE_ROLES.ORG_ADMIN,
    Constants.COOKIE_ROLES.SUPER_ADMIN,
  ].includes(Cookies.get("role"));

  const isDraftstrikeOffTransaction = transactions.some(
    (transaction) =>
      transaction.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF &&
      transaction.status === Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DRAFT
  );

  const isIntendedDissolution = transactions.find(
    (transaction) =>
      transaction.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF &&
      transaction.status ===
        Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.INTENDED_DISSOLUTION &&
      transaction?.fileUploads?.noticeOfIntendedDissolution
  );

  const canncelConversionTransaction = transactions.find(
    (transaction) =>
      transaction.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.NOTICE_TO_CANCEL_CONVERSION &&
      transaction.status === Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DRAFT
  );

  useEffect(() => {
    if (agreeToSubmit && agreeToTerms) setDisableSubmitBtn(false);
    else setDisableSubmitBtn(true);
  }, [agreeToSubmit, agreeToTerms]);

  const handleGoForward = async () => {
    setIsSubmitting(true);

    let response;
    if (isIntendedDissolution) {
      response = await dispatch(
        updateManualTransactions({
          companyId,
          data: {
            transactionIds,
            status:
              Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DISSOLUTION_NOTICE,
            //The status of the company should be changed to STRUCK OFF when complation of processing the transaction
            companyChangedFields: {
              ...isIntendedDissolution?.companyChangedFields,
              status: Constants.ENTITY_STATUS.STRUCK_OFF,
            },
          },
        })
      );
    } else {
      response = await dispatch(
        updateManualTransactions({
          companyId,
          data: {
            transactionIds,
            status,
          },
        })
      );
    }

    if (response?.error) {
      toast("error", `Failed to update the transaction.`);
      return;
    } else {
      toast(
        "success",
        `The application has been submitted for Registrar Review successfully`
      );
      if (isDraftstrikeOffTransaction) {
        const companyRes = await dispatch(
          updateCompanyAsync({
            id: companyId,
            data: {
              status: Constants.ENTITY_STATUS.STRIKE_OFF_ONGOING,
            },
          })
        );

        if (companyRes?.error) {
          toast("error", `Failed to update the transaction.`);
          return;
        } else {
          toast("success", `The company status updated to Strike Off Ongoing`);
        }
      }
      if (canncelConversionTransaction) {
        transactionIds =
          canncelConversionTransaction?.companyChangedFields
            ?.transactionToCancelDetails?._id;
        const response = await dispatch(
          updateManualTransactions({
            companyId,
            data: {
              transactionIds,
              status:
                Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.CANCELLATION_NOTICE,
            },
          })
        );

        if (response?.error) {
          toast("error", `Failed to update the transaction.`);
          return;
        } else {
          toast(
            "success",
            `The conversion transaction status updated to cancellation notice successfully`
          );
        }
      }

      history.push(`/companies/${companyId}/dashboard?manageYourCompany`);
    }

    setIsSubmitting(false);
  };

  return (
    <>
      {unresolvedComments.length > 0 && (
        <div className="border-l-4 border-yellow-400 bg-yellow-50 p-4 mt-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationIcon
                className="h-5 w-5 text-yellow-400"
                aria-hidden="true"
              />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                You can submit the application to{" "}
                {isReviewer ? "admin" : isAdmin ? `BRS` : ""} by resolving the
                remaining({unresolvedComments.length}){" "}
                {unresolvedComments.length === 1 ? "comment" : "comments"}.{" "}
              </p>
            </div>
          </div>
        </div>
      )}
      {comments.length > 0 && (
        <div className="px-8 pt-5 pb-6 mb-10 bg-white mt-6">
          <CommentsList comments={comments} toggleCommentStatus paginate />
        </div>
      )}
      <div className="flex flex-col space-y-2 align-middle mt-6">
        <div className="flex items-center form-check">
          <input
            className="float-left mt-1 mr-4 align-top transition duration-200 bg-transparent bg-center bg-no-repeat bg-contain border rounded appearance-none cursor-pointer form-check-input border-tertiary checked:bg-blue-600 checked:border-blue-600 focus:outline-none"
            type="checkbox"
            id="flexCheckDefault"
            defaultChecked={agreeToSubmit}
            onChange={({ target: { checked } }) => setAgreeToSubmit(checked)}
          />
          <label
            className="inline-block form-check-label text-tertiary"
            htmlFor="flexCheckDefault"
          >
            Are you sure you want to submit the following details to the{" "}
            {submitToLabel}
            <sup className="text-red-500">*</sup>
          </label>
        </div>
        <div className="flex items-center form-check">
          <input
            className="float-left mt-1 mr-4 align-top transition duration-200 bg-transparent bg-center bg-no-repeat bg-contain border rounded appearance-none cursor-pointer form-check-input border-tertiary checked:bg-blue-600 checked:border-blue-600 focus:outline-none"
            type="checkbox"
            id="agreeToTerms"
            defaultChecked={agreeToTerms}
            onChange={({ target: { checked } }) => setAgreeToTerms(checked)}
          />
          <label
            className="inline-block form-check-label text-tertiary"
            htmlFor="agreeToTerms"
          >
            I agree to the terms and conditions
            <sup className="text-red-500">*</sup>
          </label>
        </div>
      </div>
      <div className="mt-4 px-4 py-3  sm:px-6">
        <div className="flex justify-between space-x-2">
          <div>
            {
              <button
                type="button"
                onClick={handleGoBack}
                className="flex items-start float-right py-2 font-semibold rounded text-primary mt-7"
              >
                <span className="flex items-center mr-4 h-7">
                  <ChevronLeftIcon
                    className="flex-shrink-0 w-5 h-5"
                    aria-hidden="true"
                  />
                </span>
                <p className="font-semibold">Go Back</p>
              </button>
            }
          </div>
          {transactionIds?.length > 0 && (
            <button
              onClick={handleGoForward}
              className={`flex items-center px-4 py-2 mb-2 font-semibold mt-7
                            ${
                              !agreeToSubmit || !agreeToTerms
                                ? "cursor-not-allowed text-white rounded bg-green-focused/20"
                                : "hover:bg-green-600 hover:shadow-lg text-white rounded bg-sflPrimary"
                            } // Hover only when enabled
                          `}
            >
              {isSubmitting ? (
                <>
                  <SpinnerIcon />
                  Submitting...
                </>
              ) : (
                "Submit for Review"
              )}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
